import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaBriefcase,
  FaCalendarAlt,
  FaEdit,
  FaInfoCircle,
  FaStickyNote,
  FaTrash,
  FaUser,
} from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import { canDeleteInPortal, portalLoginEmailHint } from "./portalSession";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";
import PortalAlert from "./PortalAlert";
import PortalFieldInfo from "./PortalFieldInfo";
import PortalConfirmModal from "./PortalConfirmModal";
import { PASSWORD_POLICY_HINT, validatePasswordStrength } from "../utils/passwordPolicy";
import PortalSelect from "./PortalSelect";

const EMPLOYEE_STATUSES = ["Active", "Inactive", "Resigned", "Terminated", "On Hold"];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function displayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return displayValue(value);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function DetailItem({ label, value, children }) {
  return (
    <div className="cportal-profile-view-item">
      <span className="cportal-profile-view-label">{label}</span>
      <span className="cportal-profile-view-value">{children ?? displayValue(value)}</span>
    </div>
  );
}

function DetailSection({ icon: Icon, title, children, className = "" }) {
  return (
    <section className={`cportal-card cportal-detail-section ${className}`.trim()}>
      <h2 className="cportal-detail-section-title">
        {Icon ? <Icon className="cportal-detail-section-icon" aria-hidden="true" /> : null}
        {title}
      </h2>
      {children}
    </section>
  );
}

function initials(row) {
  const first = (row?.First_Name || "").trim().charAt(0);
  const last = (row?.Last_Name || "").trim().charAt(0);
  return `${first}${last}`.toUpperCase() || "E";
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-badge cportal-badge--success";
  if (s === "inactive" || s === "terminated" || s === "resigned") {
    return "cportal-badge cportal-badge--danger";
  }
  return "cportal-badge";
}

function fullName(row) {
  return `${(row?.First_Name || "").trim()} ${(row?.Last_Name || "").trim()}`.trim() || "—";
}

function rolesDisplay(row) {
  if (Array.isArray(row?.roles) && row.roles.length) {
    return row.roles
      .map((r) => r.role_name || r.Role_Name || r.role || r.name)
      .filter(Boolean)
      .join(", ");
  }
  if (row?.Roles) return String(row.Roles);
  return "";
}

function roleIdsFromRow(row) {
  if (Array.isArray(row?.roles) && row.roles.length) {
    return row.roles
      .map((r) => r.role_id ?? r.Role_Id ?? r.id)
      .filter((id) => id != null && id !== "")
      .map((id) => String(id));
  }
  if (Array.isArray(row?.Role_Ids) && row.Role_Ids.length) {
    return row.Role_Ids.map((id) => String(id));
  }
  return [];
}

function EmailLink({ email }) {
  const value = String(email || "").trim();
  if (!value) return "—";
  return (
    <a href={`mailto:${value}`} className="cportal-contact-link">
      {value}
    </a>
  );
}

function PhoneLink({ countryCode, phone }) {
  const number = String(phone || "").trim();
  if (!number) return "—";
  const cc = String(countryCode || "").trim();
  const label = cc ? `${cc} ${number}` : number;
  return (
    <a href={`tel:${`${cc}${number}`.replace(/[^\d+]/g, "")}`} className="cportal-contact-link">
      {label}
    </a>
  );
}

function toForm(row) {
  return {
    firstName: row?.First_Name || "",
    lastName: row?.Last_Name || "",
    companyEmail: row?.Company_Email || "",
    password: "",
    personalEmail: row?.Personal_Email || "",
    employeeId: row?.Employee_Id || "",
    countryCode: row?.Country_Code || "+91",
    phoneNumber: row?.Phone_Number || "",
    altPhone: row?.Alt_Phone || "",
    department: row?.Department || "",
    designationId: row?.Designation_Id ? String(row.Designation_Id) : "",
    roleIds: roleIdsFromRow(row),
    gender: row?.Gender || "",
    status: row?.Status || "Active",
    notes: row?.Notes || "",
  };
}

function buildPayload(form, { personLocked = false } = {}) {
  const payload = {
    Employee_Id: form.employeeId.trim(),
    Department: form.department.trim() || null,
    Designation_Id: Number(form.designationId),
    Role_Ids: (Array.isArray(form.roleIds) ? form.roleIds : [])
      .map((id) => Number(id))
      .filter((n) => !Number.isNaN(n)),
    Status: form.status || "Active",
    Notes: form.notes.trim() || null,
  };
  // A login shared with another company is managed by the person; send company fields only.
  if (personLocked) return payload;
  Object.assign(payload, {
    First_Name: form.firstName.trim(),
    Last_Name: form.lastName.trim() || null,
    Company_Email: form.companyEmail.trim(),
    Personal_Email: form.personalEmail.trim() || null,
    Country_Code: form.countryCode.trim() || null,
    Phone_Number: form.phoneNumber.trim(),
    Alt_Phone: form.altPhone.trim() || null,
    Gender: form.gender.trim() || null,
  });
  const nextPassword = (form.password || "").trim();
  if (nextPassword) {
    payload.Password = nextPassword;
  }
  return payload;
}

function validateEmployeeForm(form, originalCompanyEmail = "", personLocked = false) {
  if (personLocked) {
    if (!form.employeeId.trim()) return "Employee ID is required.";
    if (!form.designationId) return "Designation is required.";
    if (!Array.isArray(form.roleIds) || form.roleIds.length === 0) {
      return "At least one role is required.";
    }
    return "";
  }
  if (!form.firstName.trim()) {
    return "First name is required.";
  }
  if (!form.companyEmail.trim()) {
    return "Company email is required.";
  }
  if (!EMAIL_PATTERN.test(form.companyEmail.trim())) {
    return "Enter a valid company email.";
  }
  if (form.personalEmail.trim() && !EMAIL_PATTERN.test(form.personalEmail.trim())) {
    return "Enter a valid personal email.";
  }
  if (!form.employeeId.trim()) {
    return "Employee ID is required.";
  }
  const phoneError = mobileValidationMessage(form.phoneNumber, { required: true });
  if (phoneError) return phoneError;
  const altPhoneError = mobileValidationMessage(form.altPhone, { label: "Alternate phone" });
  if (altPhoneError) return altPhoneError;
  if (!form.designationId) {
    return "Designation is required.";
  }
  if (!Array.isArray(form.roleIds) || form.roleIds.length === 0) {
    return "At least one role is required.";
  }
  const nextPassword = (form.password || "").trim();
  if (nextPassword) {
    return validatePasswordStrength(nextPassword) || "";
  }
  return "";
}

function TenantEmployeeView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [employee, setEmployee] = useState(null);
  const [designations, setDesignations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const canDelete = useMemo(() => canDeleteInPortal(), []);
  const personLocked = Boolean(employee?.Shared_Login);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [empRes, metaRes] = await Promise.all([
          axiosInstance.get(`/client/employees/${id}`),
          axiosInstance.get("/client/employee-meta").catch(() => ({ data: { data: {} } })),
        ]);
        if (!active) return;
        const data = empRes.data?.data || null;
        setEmployee(data);
        setForm(toForm(data));
        if (!data) setError("Employee not found.");
        const meta = metaRes.data?.data || {};
        setDesignations(Array.isArray(meta.designations) ? meta.designations : []);
        const domainRoles = Array.isArray(meta.roles) ? meta.roles : [];
        const assigned = Array.isArray(data?.roles) ? data.roles : [];
        const byId = new Map();
        [...domainRoles, ...assigned].forEach((r) => {
          const rid = r.role_id ?? r.Role_Id;
          if (rid == null) return;
          byId.set(String(rid), {
            role_id: rid,
            Role_Id: rid,
            role_name: r.role_name || r.Role_Name || r.name || "",
            Role_Name: r.role_name || r.Role_Name || r.name || "",
          });
        });
        setRoles(Array.from(byId.values()));
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.message || "Could not load employee details.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (location.state?.edit) {
      setEditing(true);
    }
  }, [location.state?.edit]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const toggleRoleId = (roleId) => {
    const idStr = String(roleId);
    setForm((prev) => {
      const current = Array.isArray(prev.roleIds) ? prev.roleIds : [];
      const next = current.includes(idStr)
        ? current.filter((id) => id !== idStr)
        : [...current, idStr];
      return { ...prev, roleIds: next };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const validationError = validateEmployeeForm(form, employee?.Company_Email, personLocked);
    if (validationError) {
      setMessage({ type: "error", text: validationError });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await axiosInstance.put(`/client/employees/${id}`, buildPayload(form, { personLocked }));
      const data = res.data?.data;
      if (data) {
        setEmployee(data);
        setForm(toForm(data));
      }
      setEditing(false);
      setMessage({ type: "success", text: "Employee updated." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not update employee.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    setConfirmDeleteOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (deleting) return;
    setConfirmDeleteOpen(false);
  };

  const confirmDeleteEmployee = async () => {
    setDeleting(true);
    setMessage({ type: "", text: "" });
    try {
      await axiosInstance.delete(`/client/employees/${id}`);
      navigate("/portal/employees");
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not deactivate employee.",
      });
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="cportal-page">
        <p className="cportal-muted">Loading…</p>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="cportal-page">
        <button
          type="button"
          className="cportal-back-link"
          onClick={() => navigate("/portal/employees")}
        >
          <FaArrowLeft /> Back to employees
        </button>
        <div className="cportal-alert cportal-alert-error">{error || "Employee not found."}</div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="cportal-page">
        <button
          type="button"
          className="cportal-back-link"
          onClick={() => {
            setEditing(false);
            setForm(toForm(employee));
            setMessage({ type: "", text: "" });
          }}
        >
          <FaArrowLeft /> Back to employee
        </button>

        <form className="cportal-card" style={{ marginTop: 16 }} onSubmit={handleSave} noValidate>
          <div className="cportal-card-head">
            <h2>Edit Employee</h2>
          </div>
          {message.text && (
            <PortalAlert
              type={message.type === "error" ? "error" : "success"}
              message={message.text}
              onClear={() => setMessage({ type: "", text: "" })}
            />
          )}
          {personLocked && (
            <p className="cportal-muted" style={{ margin: "0 0 12px" }}>
              This person also belongs to another company. Their name, contact details and login are
              managed by them; you can change their company details (employee ID, designation, roles,
              status).
            </p>
          )}
          <div className="cportal-form-grid cportal-form-grid--3">
            <div className="cportal-field">
              <label>First name <span className="cportal-required">*</span></label>
              <input
                value={form.firstName}
                onChange={(e) => setField("firstName", e.target.value)}
                disabled={personLocked}
                required
              />
            </div>
            <div className="cportal-field">
              <label>Last name</label>
              <input
                value={form.lastName}
                onChange={(e) => setField("lastName", e.target.value)}
                disabled={personLocked}
              />
            </div>
            <div className="cportal-field">
              <label>
                Company Email <span className="cportal-required">*</span>
                {!personLocked ? (
                  <PortalFieldInfo label="About company email">
                    {portalLoginEmailHint()}
                  </PortalFieldInfo>
                ) : null}
              </label>
              <input
                type="email"
                value={form.companyEmail}
                onChange={(e) => setField("companyEmail", e.target.value)}
                disabled={personLocked}
                required
                autoComplete="off"
              />
            </div>
            {!personLocked && (
              <div className="cportal-field">
                <label>
                  Password
                  <PortalFieldInfo label="About password">
                    Optional. {PASSWORD_POLICY_HINT}
                  </PortalFieldInfo>
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setField("password", e.target.value)}
                  autoComplete="new-password"
                  placeholder="Leave blank to keep current password"
                />
              </div>
            )}
            <div className="cportal-field">
              <label>Personal Email</label>
              <input
                type="email"
                value={form.personalEmail}
                onChange={(e) => setField("personalEmail", e.target.value)}
                disabled={personLocked}
              />
            </div>
            <div className="cportal-field cportal-field-phone">
              <label>Mobile number <span className="cportal-required">*</span></label>
              <div className="cportal-phone-row">
                <input
                  className="cportal-phone-code"
                  value={form.countryCode}
                  onChange={(e) => setField("countryCode", e.target.value)}
                  disabled={personLocked}
                />
                <input
                  value={form.phoneNumber}
                  onChange={(e) => setField("phoneNumber", sanitizeMobileInput(e.target.value))}
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit number"
                  disabled={personLocked}
                  required
                />
              </div>
            </div>
            <div className="cportal-field">
              <label>Employee ID <span className="cportal-required">*</span></label>
              <input
                value={form.employeeId}
                onChange={(e) => setField("employeeId", e.target.value)}
                required
              />
            </div>
            <div className="cportal-field">
              <label>Gender</label>
              <input
                value={form.gender}
                onChange={(e) => setField("gender", e.target.value)}
                disabled={personLocked}
              />
            </div>
            <div className="cportal-field">
              <label>Alt phone</label>
              <input
                value={form.altPhone}
                onChange={(e) => setField("altPhone", sanitizeMobileInput(e.target.value))}
                disabled={personLocked}
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit number"
              />
            </div>
            <div className="cportal-field">
              <label>Department</label>
              <input value={form.department} onChange={(e) => setField("department", e.target.value)} />
            </div>
            <div className="cportal-field">
              <label>Designation <span className="cportal-required">*</span></label>
              <PortalSelect
                value={form.designationId}
                onChange={(e) => setField("designationId", e.target.value)}
                required
              >
                <option value="">Select designation</option>
                {designations.map((d) => (
                  <option key={d.Designation_Id} value={d.Designation_Id}>
                    {d.Designation_Name}
                  </option>
                ))}
              </PortalSelect>
            </div>
            <div className="cportal-field cportal-field--full">
              <label>
                Roles <span className="cportal-required">*</span>
              </label>
              {roles.length === 0 ? (
                <p className="cportal-muted">No roles available.</p>
              ) : (
                <div className="cportal-role-checklist">
                  {roles.map((r) => {
                    const rid = String(r.role_id ?? r.Role_Id);
                    return (
                      <label key={rid}>
                        <input
                          type="checkbox"
                          checked={(form.roleIds || []).includes(rid)}
                          onChange={() => toggleRoleId(rid)}
                        />
                        <span>{r.role_name || r.Role_Name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="cportal-field">
              <label>
                Status <span className="cportal-required">*</span>
              </label>
              <PortalSelect value={form.status} onChange={(e) => setField("status", e.target.value)}>
                {EMPLOYEE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </PortalSelect>
            </div>
            <div className="cportal-field cportal-field--full">
              <label>Notes</label>
              <textarea rows={3} value={form.notes} onChange={(e) => setField("notes", e.target.value)} />
            </div>
          </div>
          <div className="cportal-form-actions">
            <button
              type="button"
              className="cportal-btn cportal-btn-ghost"
              onClick={() => {
                setEditing(false);
                setForm(toForm(employee));
                setMessage({ type: "", text: "" });
              }}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="cportal-btn cportal-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="cportal-page">
      <button
        type="button"
        className="cportal-back-link"
        onClick={() => navigate("/portal/employees")}
      >
        <FaArrowLeft /> Back to employees
      </button>

      {message.text && (
        <PortalAlert
          type={message.type === "error" ? "error" : "success"}
          message={message.text}
          onClear={() => setMessage({ type: "", text: "" })}
        />
      )}

      <div className="cportal-entity-hero cportal-entity-hero--inline">
        <div className="cportal-entity-hero-left">
          <div className="cportal-entity-avatar" aria-hidden="true">
            {initials(employee)}
          </div>
          <div className="cportal-entity-hero-meta">
            <h1 className="cportal-entity-hero-name">{fullName(employee)}</h1>
            <div className="cportal-entity-hero-sub">
              <span className={statusBadgeClass(employee.Status)}>
                {displayValue(employee.Status)}
              </span>
              {rolesDisplay(employee) ? (
                <span className="cportal-entity-hero-role">{rolesDisplay(employee)}</span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="cportal-entity-hero-actions">
          <button
            type="button"
            className="cportal-btn cportal-btn-edit"
            onClick={() => setEditing(true)}
            aria-label="Edit employee"
            title="Edit employee"
          >
            <FaEdit aria-hidden="true" /> <span className="cportal-btn-text">Edit</span>
          </button>
          {canDelete && (
            <button
              type="button"
              className="cportal-btn cportal-btn-danger-outline"
              onClick={handleDelete}
              disabled={deleting || employee.Status === "Inactive"}
              aria-label="Delete employee"
              title="Delete employee"
            >
              <FaTrash aria-hidden="true" />{" "}
              <span className="cportal-btn-text">{deleting ? "Deactivating…" : "Delete"}</span>
            </button>
          )}
        </div>
      </div>

      <div className="cportal-detail-grid">
        <DetailSection icon={FaUser} title="Contact information">
          <div className="cportal-profile-view-grid">
            <DetailItem label="First name" value={employee.First_Name} />
            <DetailItem label="Last name" value={employee.Last_Name} />
            <DetailItem label="Company Email">
              <EmailLink email={employee.Company_Email} />
            </DetailItem>
            <DetailItem label="Password set" value={employee.Has_Password ? "Yes" : "No"} />
            <DetailItem label="Personal Email">
              <EmailLink email={employee.Personal_Email} />
            </DetailItem>
            <DetailItem label="Mobile number">
              <PhoneLink countryCode={employee.Country_Code} phone={employee.Phone_Number} />
            </DetailItem>
            <DetailItem label="Alt phone">
              <PhoneLink countryCode={employee.Country_Code} phone={employee.Alt_Phone} />
            </DetailItem>
          </div>
        </DetailSection>

        <DetailSection icon={FaBriefcase} title="Professional information">
          <div className="cportal-profile-view-grid">
            <DetailItem label="Employee ID" value={employee.Employee_Id} />
            <DetailItem label="Designation" value={employee.Designation} />
            <DetailItem label="Roles" value={rolesDisplay(employee)} />
            <DetailItem label="Department" value={employee.Department} />
            <DetailItem label="Gender" value={employee.Gender} />
            <DetailItem label="Date of joining">{formatDate(employee.DOJ)}</DetailItem>
          </div>
        </DetailSection>
      </div>

      <DetailSection icon={FaInfoCircle} title="Other details">
        <div className="cportal-profile-view-grid">
          <DetailItem label="Created on">
            <span className="cportal-datetime-value">
              <FaCalendarAlt aria-hidden="true" />
              {formatDateTime(employee.Created_On)}
            </span>
          </DetailItem>
          <DetailItem label="Updated on">
            <span className="cportal-datetime-value">
              <FaCalendarAlt aria-hidden="true" />
              {formatDateTime(employee.Updated_On)}
            </span>
          </DetailItem>
        </div>
      </DetailSection>

      <DetailSection icon={FaStickyNote} title="Notes">
        <p className="cportal-notes-text">{String(employee.Notes || "").trim() || "—"}</p>
      </DetailSection>

      <PortalConfirmModal
        open={confirmDeleteOpen}
        title="Deactivate Employee"
        message={
          <p>
            Deactivate <strong>{displayValue(employee.First_Name)} {employee.Last_Name || ""}</strong>?
            Their status will be set to Inactive.
          </p>
        }
        confirming={deleting}
        confirmingLabel="Deactivating…"
        onConfirm={confirmDeleteEmployee}
        onClose={closeDeleteConfirm}
      />
    </div>
  );
}

export default TenantEmployeeView;
